import {
  Box,
  Checkbox,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  NumberInput,
  NumberInputField,
  Text,
  VStack,
  SimpleGrid,
  useColorModeValue,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";

const PriceSection = ({ priceData, onUpdate }) => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const labelBgColor = useColorModeValue("gray.100", "gray.700");
  const totalBgColor = useColorModeValue("blue.50", "blue.900");

  const [price, setPrice] = useState({
    price: 0,
    extra: 0,
    discount: 0,
    paid: 0,
    total: 0,
    balance: 0,
    quote: false,
  });

  useEffect(() => {
    if (priceData) {
      setPrice(priceData);
    }
  }, [priceData]);

  const calculateTotals = (updatedPrice) => {
    const total = (updatedPrice.price || 0) + (updatedPrice.extra || 0) - (updatedPrice.discount || 0);
    const balance = total - (updatedPrice.paid || 0);
    return { ...updatedPrice, total, balance };
  };

  const handleChange = (field, value) => {
    const numValue = field === "quote" ? value : parseFloat(value) || 0;
    const updated = { ...price, [field]: numValue };
    const withTotals = calculateTotals(updated);
    setPrice(withTotals);
    // Map to uppercase field names for parent
    onUpdate({
      PRICE: withTotals.price,
      EXTRA: withTotals.extra,
      DISCOUNT: withTotals.discount,
      PAID: withTotals.paid,
      TOTAL: withTotals.total,
      BALANCE: withTotals.balance,
      QUOTE: withTotals.quote ? 1 : 0,
    });
  };

  const handleQuoteChange = (checked) => {
    const updated = { ...price, quote: checked };
    if (checked) {
      updated.paid = 0;
    }
    const withTotals = calculateTotals(updated);
    setPrice(withTotals);
    // Map to uppercase field names for parent
    onUpdate({
      PRICE: withTotals.price,
      EXTRA: withTotals.extra,
      DISCOUNT: withTotals.discount,
      PAID: withTotals.paid,
      TOTAL: withTotals.total,
      BALANCE: withTotals.balance,
      QUOTE: withTotals.quote ? 1 : 0,
    });
  };

  const formatCurrency = (value) => {
    return value.toFixed(2);
  };

  return (
    <Box bg={bgColor} p={3} borderRadius='md' border='1px' borderColor={borderColor} height='100%'>
      <Heading size='sm' bg={labelBgColor} p={2} borderRadius='md' mb={3}>
        Price
      </Heading>

      <VStack spacing={2} alignItems='stretch'>
        <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={2}>
          <FormControl>
            <FormLabel fontSize='xs'>Price</FormLabel>
            <NumberInput
              size='sm'
              value={price.price || 0}
              onChange={(valueString) => handleChange("price", valueString)}
              precision={2}
              step={0.01}
            >
              <NumberInputField placeholder='0.00' />
            </NumberInput>
          </FormControl>

          <FormControl>
            <FormLabel fontSize='xs'>Extra</FormLabel>
            <NumberInput
              size='sm'
              value={price.extra || 0}
              onChange={(valueString) => handleChange("extra", valueString)}
              precision={2}
              step={0.01}
            >
              <NumberInputField placeholder='0.00' />
            </NumberInput>
          </FormControl>

          <FormControl>
            <FormLabel fontSize='xs'>Discount</FormLabel>
            <NumberInput
              size='sm'
              value={price.discount || 0}
              onChange={(valueString) => handleChange("discount", valueString)}
              precision={2}
              step={0.01}
            >
              <NumberInputField placeholder='0.00' />
            </NumberInput>
          </FormControl>
        </SimpleGrid>

        <Box bg={totalBgColor} p={2} borderRadius='md'>
          <HStack justifyContent='space-between'>
            <Text fontWeight='bold' fontSize='lg'>
              Total:
            </Text>
            <Text fontWeight='bold' fontSize='lg'>
              ${formatCurrency(price.total || 0)}
            </Text>
          </HStack>
        </Box>

        <FormControl isDisabled={price.quote}>
          <FormLabel fontSize='xs'>Paid</FormLabel>
          <NumberInput
            size='sm'
            value={price.paid || 0}
            onChange={(valueString) => handleChange("paid", valueString)}
            precision={2}
            step={0.01}
            isDisabled={price.quote}
          >
            <NumberInputField placeholder='0.00' />
          </NumberInput>
        </FormControl>

        <Box bg={totalBgColor} p={2} borderRadius='md'>
          <HStack justifyContent='space-between'>
            <Text fontWeight='bold' fontSize='lg'>
              Balance:
            </Text>
            <Text fontWeight='bold' fontSize='lg' color={price.balance > 0 ? "red.500" : "green.500"}>
              ${formatCurrency(price.balance || 0)}
            </Text>
          </HStack>
        </Box>

        <Checkbox isChecked={price.quote} onChange={(e) => handleQuoteChange(e.target.checked)}>
          This is a Quote
        </Checkbox>
      </VStack>
    </Box>
  );
};

export default PriceSection;
